import { createRouter, createWebHistory } from 'vue-router'
import { useToast } from 'vuestic-ui'

import { useUserStore } from '../store/user'
import { getSessionManager } from '../utils/sessionManager'
import {
  CONTEXT_REDIRECT_EXEMPT_ROUTES,
  CONTEXT_SELECTION_ROUTES,
  resolveRequiredContextRoute,
} from '../utils/contextSelection'
import { authorizedLandingRouteName } from '../utils/authorizedLanding'
import { resolveUnauthorizedRouteRedirect, type PermissionAccessCheck } from '../utils/routeAccess'
import { endRouteNavigationBusy, startRouteNavigationBusy } from '../composables/useRouteNavigationBusy'
import routes from './routes'

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    }
    // For some reason using documentation example doesn't scroll on page navigation.
    if (to.hash) {
      return { el: to.hash, behavior: 'smooth' }
    } else {
      window.scrollTo(0, 0)
    }
  },
  routes,
})

function shouldShowNavigationBusy(
  to: { path: string; name?: unknown },
  from: { path: string; name?: unknown; matched: unknown[] },
) {
  // Skip initial app load (blank chrome) and same-page / hash-only changes.
  if (from.matched.length === 0) {
    return false
  }
  return to.path !== from.path || to.name !== from.name
}

// Protecting routes.
// Use return-based guards only — mixing async/await with next() can leave the
// router stuck when post-login pushes cancel an in-flight navigation.
router.beforeEach(async (to, from) => {
  if (shouldShowNavigationBusy(to, from)) {
    startRouteNavigationBusy()
  }

  const sessionManager = getSessionManager()

  if (!to.matched.some((record) => record.meta.requiresAuth)) {
    // Non-protected routes (e.g. login): session-conflict UI is handled elsewhere.
    if (to.name === 'login' && sessionManager.hasOtherTabSession()) {
      // Session manager shows the conflict message via the event listener.
    }
    return true
  }

  try {
    const userStore = useUserStore()
    // Reuse hydrated auth from login/context steps; avoid a network round-trip on
    // every sidebar click (overlapping awaits were freezing navigation after login).
    const authenticated = await userStore.dispatchGetAuthenticatedUser({ force: false })
    if (!authenticated) {
      return { name: 'login' }
    }

    const toName = typeof to.name === 'string' ? to.name : ''
    if (CONTEXT_REDIRECT_EXEMPT_ROUTES.has(toName)) {
      return true
    }

    const requiredRoute = resolveRequiredContextRoute(userStore.authUser)
    const hasModule = (slug: string) => userStore.hasModule(slug)
    const hasPermission: PermissionAccessCheck = (action, subject) => userStore.hasPermission(action, subject)

    if (!requiredRoute) {
      // `/home` has no route-record redirect; those run before this guard hydrates auth.
      if (toName === 'home' || toName === 'branch-selection' || toName === 'location-selection') {
        return { name: authorizedLandingRouteName() }
      }

      // Sidebar-gated pages re-check module and permission access on every navigation,
      // including typed URLs. Dashboard landings are included in that map.
      const unauthorizedRedirect = resolveUnauthorizedRouteRedirect(
        toName,
        userStore.defaultDashboard,
        hasModule,
        hasPermission,
      )
      if (unauthorizedRedirect) {
        if (from.matched.length > 0) {
          useToast().init({ message: 'You do not have access to this page', color: 'danger' })
        }
        return { name: unauthorizedRedirect }
      }

      return true
    }

    if (toName !== requiredRoute) {
      if (requiredRoute === 'business-options-menu' && !CONTEXT_SELECTION_ROUTES.has(toName)) {
        useToast().init({ message: 'User is not active in any business', color: 'danger' })
      }
      return { name: requiredRoute }
    }

    return true
  } catch {
    return { name: 'login' }
  }
})

router.afterEach(() => {
  endRouteNavigationBusy()
})

router.onError(() => {
  endRouteNavigationBusy()
})

export default router
